<template>
<!-- eslint-disable max-len -->
<edit-page
  name="Dashboard Profile"
  model-name="profile"
  param="profileId"
  page-prefix="/blade/dashboards/profiles"
  :create="createDashboardProfile"
  :fetch="fetchProfile"
  :update="updateDashboardProfile"
  :remove="deleteDashboardProfile"
  :stub="profile"
  :validator="validator"
  :utils="utils">
<template slot-scope="{ model: profile, validator }">
  <template v-if="loading">
    <blade-spinner nested no-background/>
  </template>
  <template v-else>
    <text-input label v-model.trim="profile.name" :validator="validator.name" required autofocus>Name</text-input>
    <select-input
      v-model="profile.dashboard._id"
      :validator="validator.dashboard._id"
      :options="dashboardList"
      use="_id"
      display="name"
      required
      label
      autofocus
      @input="refreshCachedCapabilityList(profile)"
    >
      Dashboard (note: changing the dashboard will reset all metric mappings)
    </select-input>

    <h2>Metric Mapping</h2>
    <select-input
      v-model="gameIdForMetrics"
      :validator="validator.dashboard._id"
      :options="gameList"
      use="_id"
      display="name"
      label
    >
      Target Gamulation (note: only for filtering the available set of metrics; does not save)
    </select-input>
    <b-btn
      :disabled="!gameForMetrics"
      @click="populateMissingMetricsFromGamulation(profile)"
    >
      Auto Populate Missing Mappings from Selected Gamulation
    </b-btn>

    <!--
    <h5>Debug</h5>
    <div class="mapping-root">processed caps: {{dashboardCapabilities}}</div>
    <div class="mapping-root">resulting mappings: {{profile.mappings}}</div>
    <div v-if="gameForMetrics" class="mapping-root">game metrics ({{gameForMetrics.metrics.length}}): {{gameForMetrics.metrics}}</div>
    <div class="mapping-root">available metrics: {{availableMetrics}}</div>
    -->

    <h4>Mappings</h4>
    <div class="mapping-root" :style="mapping.isSubCapability ? 'margin-left: 30px;' : ''" v-for="(mapping, idx) in dashboardCapabilities" :key="`map-${idx}`">
      <div style="display: flex; flex-direction: row;">
        <div :style="computeStyleForCap(mapping.capability)"/>
        <h4 style="margin-left: 10px;">{{mapping.capability.name}}</h4>
      </div>

      <b-form>
        <b-form-group label="B.L.A.D.E. Metric">
          <b-input-group>
            <b-form-select v-model="mapping.metric" :options="metricSelectOptions"/>
            <b-input-group-append v-if="mapping.metric">
              <router-link :to="`/blade/metrics/${mapping.metric._id}/edit`" target="_blank" class="btn">Info</router-link>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-form>
      <b-alert variant="warning" show v-if="!mapping.metric">Warning: No B.L.A.D.E. metric is assigned to this mapping.  This will show up as the minimum value for the dashboard.</b-alert>

      <select-input
        v-model="mapping.conversion"
        :options="conversionTypes"
        label
      >
        Conversion (optional)
      </select-input>
    </div>

    <select-input
      label
      v-model="profile.timeMetric"
      :options="availableMetrics"
      use="_id"
      :display="m => m.name"
      :to="(id) => { return `/blade/metrics/${id}/edit`; }"
    >
      Time At Company Metric (optional)
    </select-input>
    <select-input
      label
      v-model="profile.jobMetric"
      :options="availableMetrics"
      use="_id"
      :display="m => m.name"
      :to="(id) => { return `/blade/metrics/${id}/edit`; }"
    >
      Job Metric (optional)
    </select-input>
    <checkbox-input v-model="profile.mapJobCodeToDisplayValues">Map Job Code To Display Values</checkbox-input>
    <select-input
      label
      v-model="profile.departmentMetric"
      :options="availableMetrics"
      use="_id"
      :display="m => m.name"
      :to="(id) => { return `/blade/metrics/${id}/edit`; }"
    >
      Department Metric (optional)
    </select-input>
    <checkbox-input v-model="profile.mapDepartmentCodeToDisplayValues">Map Department Code To Display Values</checkbox-input>
    <number-input v-model="profile.remappingRangeMin" label>Remap Range Min (optional, used by any Remap conversions)</number-input>
    <number-input v-model="profile.remappingRangeMax" label>Remap Range Max (optional, used by any Remap conversions)</number-input>
  </template>
</template>
</edit-page>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';

import { unused } from '../../../utils/utils';

import EditPage from '../../ui/pages/EditPage';
import NarrativeInput from '../../ui/inputs/NarrativeInput';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import NumberInput from '../../ui/inputs/NumberInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';

export default {
  components: {
    EditPage,
    NarrativeInput,
    ArrayInput,
    NumberInput,
    SelectInput,
    TextInput,
    CheckboxInput
  },
  async mounted() {
    await this.loadData();
  },
  watch: {
    gameIdForMetrics() {
      this.loadMetricFilterGameFromId();
    },
    dashboardCapabilities: {
      deep: true,
      handler() {
        // alert('updating cached profile');
        this.updateCachedProfileWithInternalCapabilities();
      },
    }
  },
  computed: {
    availableMetrics() {
      let res;
      console.log('recomputing available metrics');
      if (this.gameForMetrics) {
        console.log('game found');
        res = this.metricList.filter(
          metric => this.gameForMetrics.game.startingTeamMetrics.find(
            gameMetric => metric._id === gameMetric._id
          ) || this.gameForMetrics.game.startingIndividualMetrics.find(
            gameMetric => metric._id === gameMetric._id
          )
        );
      } else {
        res = [...this.metricList];
      }
      res.sort((a, b) => a.name.localeCompare(b.name));
      return res;
    },
    metricSelectOptions() {
      return [{ text: 'None', value: null },
        ...this.availableMetrics.map(metric => ({
          text: metric.name,
          value: metric
        }))];
    }
  },
  data() {
    return {
      loading: false,
      dashboardList: [],
      metricList: [],
      gameList: [],
      conversionTypes: [
        'None',
        'Z-Score',
        'Normalized',
        'Z-Score + Remap',
        'Normalized + Remap',
      ],

      targetProfile: null,

      // Note: This is the intermediate array that is actually interacted with.
      // Changes in this field are automatically propagated to the actual profile object,
      // which is much more minimal.
      dashboardCapabilities: [],
      gameIdForMetrics: null,
      gameForMetrics: null,

      utils: {
        unused
      },
      profile: {
        _id: '',
        name: '',
        dashboard: { _id: null },
        mappings: [],
        timeMetric: null,
        jobMetric: null,
        mapJobCodeToDisplayValues: false,
        departmentMetric: null,
        mapDepartmentCodeToDisplayValues: false,
      },
      validator: {
        name: { required },
        dashboard: {
          _id: { required }
        },
      }
    };
  },
  methods: {
    ...mapActions([
      'flashDanger',
      'getDashboards',
      'getDashboardProfile',
      'createDashboardProfile',
      'updateDashboardProfile',
      'deleteDashboardProfile',
      'getGames',
      'getGame',
      'getMetrics',
    ]),

    updateMetricInMapping(mapping, metricId) {
      if (!metricId) {
        mapping.metric = null;
      } else {
        const metric = this.metricList.find(m => m._id === metricId);
        mapping.metric = metric || null;
        console.log('updateMetricInMapping below');
        console.log({ mapping, metricId, metric });
      }
      this.updateCachedProfileWithInternalCapabilities();
    },

    computeStyleForCap(capability) {
      return {
        'background-color': capability.color,
        width: '32px',
        height: '32px'
      };
    },

    refreshCachedCapabilityList(profile) {
      console.log('Refreshing cached capability list');
      // If it hasn't loaded yet, then delay.  This should only ever come up during hot reloading.
      // Not the best solution, bu it works.
      if (this.loading || this.metricList.length === 0) {
        console.log('waiting for dashboards and metrics to be loaded');
        setTimeout(() => { this.refreshCachedCapabilityList(profile); }, 300);
        return;
      }

      const dashboardId = profile.dashboard._id;
      if (!dashboardId) {
        // eslint-disable-next-line no-alert
        alert('Failed to initialize authoring form - dashboard id not found.  Please notify a developer of this error.');
        return;
      }

      const dashboard = this.dashboardList.find(dash => dash._id === dashboardId);
      const targetSet = [];

      console.log({ mappings: profile.mappings });
      const pushCap = (capability, isSubCapability) => {
        const associatedMapping = profile.mappings.find(mapping => mapping.capabilityId === capability._id);
        const metric = associatedMapping && associatedMapping.metricId
          ? this.metricList.find(met => met._id === associatedMapping.metricId)
          : null;

        console.log(`pushing capability: ${JSON.stringify({
          capabilityId: capability._id,
          capabilityName: capability.name,
          metric,
          conversion: associatedMapping ? associatedMapping.conversion : null,
          isSubCapability
        })}`);

        targetSet.push({
          capability,
          metric,
          conversion: associatedMapping ? associatedMapping.conversion : null,
          isSubCapability
        });
      };

      // eslint-disable-next-line no-restricted-syntax
      for (const category of dashboard.capabilities) {
        // eslint-disable-next-line no-restricted-syntax
        for (const capability of category.capabilities) {
          pushCap(capability, false);

          // eslint-disable-next-line no-restricted-syntax
          for (const subCapabilitiy of capability.subCapabilities) {
            pushCap(subCapabilitiy, true);
          }
        }
      }

      console.log({ targetSet: JSON.parse(JSON.stringify(targetSet)) });
      this.dashboardCapabilities = targetSet;
      this.updateCachedProfileWithInternalCapabilities(profile);
    },

    updateCachedProfileWithInternalCapabilities(profile) {
      if (profile) {
        this.$set(this, 'targetProfile', profile);
      }

      if (this.targetProfile) {
        console.log('Updating cached profile');
        this.$set(this.targetProfile, 'mappings', this.dashboardCapabilities.map(
          fullData =>
            ({
              metricId: fullData.metric ? fullData.metric._id : null,
              capabilityId: fullData.capability._id,
              conversion: fullData.conversion || undefined,
            })
        ));
        console.log({ targetProfile: this.targetProfile });
      }
    },

    populateMissingMetricsFromGamulation() {
      let updates = 0;
      console.log({ dashboardCapabilities: this.dashboardCapabilities, availableMetrics: this.availableMetrics });
      // eslint-disable-next-line no-restricted-syntax
      for (const mapping of this.dashboardCapabilities) {
        if (!mapping.metric) {
          // eslint-disable-next-line no-restricted-syntax
          for (const metric of this.availableMetrics) {
            if (metric.name.toLowerCase() === mapping.capability.name.toLowerCase()
            || metric.title.toLowerCase() === mapping.capability.name.toLowerCase()) {
              console.log(`Automatically updated ${mapping.capability.name} to use metric`);
              mapping.metric = metric;
              updates += 1;
              break;
            }
          }
        }
      }
      if (updates > 1) {
        // eslint-disable-next-line no-alert
        alert(`Updated ${updates} mappings`);
      } else if (updates === 1) {
        // eslint-disable-next-line no-alert
        alert('Updated 1 mapping');
      } else {
        // eslint-disable-next-line no-alert
        alert('No new mappings found');
      }
      this.updateCachedProfileWithInternalCapabilities();
    },

    // Wrapper so that we can set up the internal capability list
    async fetchProfile(tg) {
      const profile = await this.getDashboardProfile(tg);
      console.log({ profile: JSON.parse(JSON.stringify(profile)) });
      if (profile && profile.profile) {
        console.log({ profileFromFetch: JSON.parse(JSON.stringify(profile)) });
        this.refreshCachedCapabilityList(profile.profile);
      }
      return profile;
    },

    // Split out for convenience/cleanliness with Promise.all()
    async loadDashboards() {
      const dashboards = await this.getDashboards();
      this.$set(this, 'dashboardList', dashboards);
    },
    async loadMetrics() {
      const metrics = await this.getMetrics();
      this.$set(this, 'metricList', metrics);
    },
    async loadGames() {
      const games = await this.getGames();
      this.$set(this, 'gameList', games);
    },
    async loadMetricFilterGameFromId() {
      if (this.gameIdForMetrics) {
        try {
          const game = await this.getGame({ gameId: this.gameIdForMetrics });
          if (!game) {
            throw new Error('Game not found');
          }
          this.$set(this, 'gameForMetrics', game);
          console.log({ game });
        } catch (err) {
          // eslint-disable-next-line no-alert
          alert('Failed to load game data');
          this.gameIdForMetrics = null;
          this.$set(this, 'gameForMetrics', null);
        }
      } else {
        this.$set(this, 'gameForMetrics', null);
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const promises = [this.loadDashboards(), this.loadMetrics(), this.loadGames()];
        await Promise.all(promises);
      } catch (err) {
        this.flashDanger('Failed to load dashboards');
        console.error('Failed to load dashboards, error below:');
        console.error({ err });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.mapping-root
{
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.5);
  padding: 10px;
  margin-bottom: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/dashboards/pages/DashboardProfileEdit.vue